import { Trans } from "react-i18next";
import { Modal, Input, message } from "antd";
import { t } from "i18next";
import { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { deleteCollection, saveCollection } from "@apis/http/api";
import { CommonButton } from "@components/CommonButton";
import report from "@report";

export const getCurrentDateFormattedFn = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

interface SaveScriptBtnProps {
  disabled?: boolean;
  className?: string;
  data?: any; // 保存的脚本data
  activeScript?: number; // 脚本1 脚本2
  showIndex?: boolean;
  handleSave?: (data: any) => void;
}

const SaveScriptBtn = ({ disabled, data, showIndex, activeScript = 0, handleSave }: SaveScriptBtnProps) => {
  const location = useLocation();
  const [saveModelOpen, setSaveModelOpen] = useState(false);
  const [scriptName, setScriptName] = useState(getCurrentDateFormattedFn());
  const [messageApi, contextHolder] = message.useMessage();
  const [countdown, setCountdown] = useState<number | null>(null);
  const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);

  // 是否是 script-review 页面
  const isScriptReviewPage = location.pathname === "/script-review";

  const handleSaveScript = async () => {
    try {
      if (scriptName.length === 0 || scriptName.length > 50) {
        return;
      }

      const res: any = await saveCollection({
        type: 2,
        keyword: data.videoId,
        params: {
          isCollect: true,
          id: data.videoId,
          requestId: data.requestId,
          videoSource: data.videoSource,
          scriptType: 2,
          name: scriptName,
          productId: data.product_id,
          productName: data.product_title,
          scenes: data.video_scripts?.map((item: any) => item.scene),
          translate: data.translate,
          activeScript: (activeScript % 2) + 1,
          from: window.location.pathname,
          category: data.category,
        },
      } as any);
      report.click({ kalo_type: location.pathname === "/insight" ? "save_to_favorites" : "save" });

      messageApi.open({
        type: "success",
        content: (
          <span className="text-kds-fg-soft">
            <Trans
              components={{
                b: <span className="font-semibold text-kds-accent-fg" />,
              }}
              defaults="Save success！You can view it in <b>Library</b>"
            ></Trans>
          </span>
        ),
      });
      setSaveModelOpen(false);
      delete res.video_scripts;
      handleSave && handleSave(res);
    } catch (error) {
      messageApi.open({
        type: "error",
        content: (
          <span className="text-kds-fg-soft">
            <Trans>{(error as any).message || error || "Save failed."}</Trans>
          </span>
        ),
      });
    }
  };

  const handleUnsaveScript = async () => {
    try {
      const res = await deleteCollection({ id: data.collectionId } as any);
      if (!res) {
        throw new Error("Sorry, something went wrong, you can try again later");
      }
      handleSave?.({ activeScript: (activeScript % 2) + 1, isCollect: false });

      // script-review 页面取消关注后，倒计时3秒关闭页面
      if (isScriptReviewPage) {
        setCountdown(3);
        countdownTimerRef.current = setInterval(() => {
          setCountdown((prev) => {
            if (prev === null || prev <= 1) {
              if (countdownTimerRef.current) {
                clearInterval(countdownTimerRef.current);
              }
              window.close();
              return null;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        message.success(t("Unfollowed successfully!"));
      }
    } catch (error) {
      messageApi.open({
        type: "error",
        content: (
          <>
            <Trans>{(error as any).message || error || "Save failed."}</Trans>
          </>
        ),
      });
    }
  };

  const handleClick = async () => {
    if (disabled) return;
    report.event("script_favorite");

    if (data.isCollect) {
      handleUnsaveScript();
    } else {
      setSaveModelOpen(true);
    }
  };

  useEffect(() => {
    if (data?.category) {
      setScriptName(data?.category?.split(" > ").pop() + "_" + getCurrentDateFormattedFn());
    }
  }, [data?.category]);

  // 清理定时器
  useEffect(() => {
    return () => {
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
      }
    };
  }, []);

  return (
    <>
      {contextHolder}
      <CommonButton size="large" disabled={disabled} autoPath={false} onClick={handleClick}>
        {showIndex ? (
          <>
            {data?.isCollect ? (
              <>
                [
                <Trans i18nKey="script_no" values={{ script_no: activeScript + 1 }} defaults="Script {{script_no}}" />]
                <span>
                  <Trans>Saved</Trans>
                </span>
              </>
            ) : (
              <>
                <span>
                  <Trans>Save</Trans>
                </span>
                [
                <Trans i18nKey="script_no" values={{ script_no: activeScript + 1 }} defaults="Script {{script_no}}" />]
              </>
            )}
          </>
        ) : (
          <Trans>{data?.isCollect ? "Script Saved" : "Save to Library"}</Trans>
        )}
      </CommonButton>
      {saveModelOpen && (
        <Modal
          title={<Trans>Save to Library</Trans>}
          open={saveModelOpen}
          onCancel={() => setSaveModelOpen(false)}
          width={412}
          footer={null}
          centered={true}
        >
          <Input
            value={scriptName}
            className="border-kds-border"
            placeholder={t("Search by keyword or product link (Kalodata or TikTok)")}
            onChange={(e) => {
              setScriptName(e.target.value);
            }}
          />
          {scriptName.length > 50 && (
            <div className="text-first mt-[4px]">
              <Trans>String must contain at most 50 character(s) </Trans>
            </div>
          )}
          <div className="flex justify-end gap-[8px] mt-[12px]">
            <CommonButton
              size="large"
              className="w-[144px]"
              onClick={() => {
                setSaveModelOpen(false);
              }}
            >
              <Trans>Cancel</Trans>
            </CommonButton>
            <CommonButton
              size="large"
              type="primary"
              className="w-[144px]"
              disabled={scriptName.length === 0 || scriptName.length > 50}
              onClick={handleSaveScript}
            >
              <Trans>Save</Trans>
            </CommonButton>
          </div>
        </Modal>
      )}
      {/* 取消关注后的倒计时关闭页面弹窗 */}
      <Modal
        open={countdown !== null}
        closable={false}
        footer={null}
        centered={true}
        width={320}
        mask={{ closable: false }}
      >
        <div className="text-center py-[16px]">
          <div className="text-[16px] font-medium text-kds-fg mb-[12px]">
            <Trans>Unfollowed successfully!</Trans>
          </div>
          <div className="text-[14px] text-kds-fg-subtle">
            <Trans
              i18nKey="page_close_countdown"
              values={{ seconds: countdown }}
              defaults="Page will close in {{seconds}} seconds"
            />
          </div>
        </div>
      </Modal>
    </>
  );
};

export default SaveScriptBtn;
